import { createConsumer } from '@rails/actioncable';
import {
  SAMPLE_RATE,
  FRAME_SAMPLES,
  FRAME_MS,
  FRAME_BYTES,
  accumulateFrames,
  base64ToArrayBuffer,
  s16LEToFloat32,
  resampleTo16k,
  isExactFrameBytes,
  boundedNextPlaybackTime,
} from 'dashboard/helper/stackZapPcm';

// SiteUP ActionCable PCM media session for StackZap.
// Never reads VoipDevice / StackZap URL / API keys / provider credentials.
//
// Recording: mixes local mic + decoded remote PCM into a MediaStreamDestination
// and MediaRecorder only after durable connected/in_progress evidence.
// Media transport may open earlier (outbound ring); recorder stays unarmed until then.
// Does not open a second mic.

const HEARTBEAT_MS = 20_000; // below lease TTL (45–90s)
const LEAD_SECONDS = FRAME_MS / 1000;
const CONNECT_TIMEOUT_MS = 10_000;
const RECORDING_TIMESLICE_MS = 1000;
/** Deterministic backoff for post-connect transport recovery (ms). */
const RECONNECT_DELAYS_MS = [250, 500, 1000, 2000];
/**
 * Permanent fail-closed codes in every phase (initial + reconnect).
 * Auth/forbidden/invalid must never resubscribe.
 */
const NONRECOVERABLE_ERROR_CODES = new Set([
  'invalid_frame',
  'unauthorized',
  'forbidden',
  'auth_failed',
  'invalid_token',
  'media_forbidden',
  'call_not_found',
  'missing_session_params',
  // Irrecoverable only when Web Audio cannot be constructed at all.
  // A live-but-suspended context is recoverable via the next user gesture.
  'audio_context_unavailable',
]);
/**
 * Lease/subscription contention: fail-closed on initial connect, but retryable
 * within the same bounded backoff after a previously successful media session.
 * Typical race: browser resubscribes at 250ms while the prior ActionCable
 * channel still holds the Redis media lease (media_in_use), or ActionCable
 * rejected() without a protocol code (mapped to media_rejected).
 */
const RECONNECT_CONTENTION_CODES = new Set(['media_in_use', 'media_rejected']);
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];
/** Enumerated AudioContext states safe for diagnostics (no PII/payload). */
const AUDIO_CONTEXT_STATES = new Set([
  'suspended',
  'running',
  'closed',
  'interrupted',
]);

/** @type {number} */
let sessionEpoch = 0;

/** @type {null | object} */
let session = null;

/**
 * Gesture-unlocked AudioContext held until a media session claims it.
 * Created/resumed on the first click handler so accept/GUM awaits cannot
 * spend user activation before the graph exists.
 * Ownership is call-scoped: a second attempt cannot retag/steal an owned holder.
 * @type {AudioContext|null}
 */
let prewarmedAudioContext = null;

/**
 * call_record_id that owns the prewarmed context (create→join window).
 * Null = unowned lease (outbound create-in-flight unlock only).
 * @type {number|string|null}
 */
let prewarmedCallRecordId = null;

function sameCallRecordId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b) || Number(a) === Number(b);
}

function mediaAbortError(code) {
  const err = new Error(code);
  err.name = 'MediaSessionError';
  err.code = code;
  return err;
}

function cableUrl() {
  const host =
    (typeof window !== 'undefined' && window.chatwootConfig?.websocketURL) ||
    '';
  return host ? `${host}/cable` : undefined;
}

function isCurrent(epoch) {
  return session && session.epoch === epoch && sessionEpoch === epoch;
}

function stopTracks(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach(t => t.stop());
  } catch {
    /* ignore */
  }
}

/**
 * @param {string|null|undefined} code
 * @param {{ hadConnected?: boolean }} [opts]
 *   hadConnected: session already completed an authenticated media connect.
 *   When true, lease-contention codes (media_in_use / media_rejected) are
 *   retryable. When false (initial connect), they remain fail-closed.
 */
function isNonrecoverableErrorCode(code, { hadConnected = false } = {}) {
  if (!code) return false;
  const normalized = String(code).trim().toLowerCase();
  if (NONRECOVERABLE_ERROR_CODES.has(normalized)) return true;
  if (normalized.includes('unauthorized') || normalized.includes('forbidden')) {
    return true;
  }
  if (normalized.includes('auth_') || normalized.endsWith('_auth')) {
    return true;
  }
  // Contention is fatal only before the first successful media connect.
  if (RECONNECT_CONTENTION_CODES.has(normalized)) {
    return !hadConnected;
  }
  return false;
}

function clearReconnectTimer(s) {
  if (!s?.reconnectTimer) return;
  clearTimeout(s.reconnectTimer);
  s.reconnectTimer = null;
}

function clearSessionTimers(s) {
  if (!s) return;
  if (s.heartbeatTimer) {
    clearInterval(s.heartbeatTimer);
    s.heartbeatTimer = null;
  }
  if (s.connectTimer) {
    clearTimeout(s.connectTimer);
    s.connectTimer = null;
  }
  clearReconnectTimer(s);
}

/** Settle pending start() at most once. No err = local cancel (resolve). */
function settlePendingConnect(s, err) {
  if (!s?.settleConnect) return;
  const settle = s.settleConnect;
  s.settleConnect = null;
  settle(err);
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return null;
  }
  return RECORDER_MIME_CANDIDATES.find(t => MediaRecorder.isTypeSupported(t));
}

function armRecorder(s) {
  // Idempotent: never arm a second MediaRecorder on duplicate connected events.
  if (
    !s?.audioContext ||
    !s.sourceNode ||
    !s.active ||
    s.mediaRecorder ||
    s.recordingStarted ||
    s.recorderFinalized
  ) {
    return false;
  }

  try {
    s.mixDestination = s.audioContext.createMediaStreamDestination();
    s.sourceNode.connect(s.mixDestination);
  } catch {
    s.recordingUnavailableReason = 'mix_failed';
    s.mixDestination = null;
    return false;
  }

  const mimeType = pickRecorderMimeType();
  if (!mimeType) {
    s.recordingUnavailableReason = 'media_recorder_unsupported';
    return false;
  }

  try {
    s.recorderChunks = [];
    s.mediaRecorder = new MediaRecorder(s.mixDestination.stream, { mimeType });
    s.recorderMimeType = mimeType;
    s.mediaRecorder.ondataavailable = event => {
      // Keep chunks until finalize completes — do not clear on stop race.
      if (event.data && event.data.size > 0) {
        s.recorderChunks.push(event.data);
      }
    };
    s.mediaRecorder.start(RECORDING_TIMESLICE_MS);
    s.recordingStarted = true;
    return true;
  } catch {
    s.mediaRecorder = null;
    s.recorderChunks = [];
    s.recordingUnavailableReason = 'media_recorder_failed';
    return false;
  }
}

/**
 * Finalize MediaRecorder exactly once. Returns a Blob or null.
 * Chunks are discarded only after the final blob is built.
 */
function finalizeRecorder(s) {
  if (!s || s.recorderFinalized) {
    return Promise.resolve(s?.finalRecordingBlob || null);
  }
  s.recorderFinalized = true;

  if (!s.mediaRecorder) {
    s.finalRecordingBlob = null;
    return Promise.resolve(null);
  }

  const recorder = s.mediaRecorder;
  return new Promise(resolve => {
    const finish = () => {
      const chunks = s.recorderChunks || [];
      let blob = null;
      if (chunks.length > 0) {
        const type = chunks[0].type || s.recorderMimeType || 'audio/webm';
        blob = new Blob(chunks, { type });
        if (!blob.size) blob = null;
      }
      s.finalRecordingBlob = blob;
      // Discard only after blob materialization.
      s.recorderChunks = [];
      s.mediaRecorder = null;
      resolve(blob);
    };

    if (recorder.state === 'inactive') {
      finish();
      return;
    }

    recorder.addEventListener('stop', finish, { once: true });
    try {
      recorder.stop();
    } catch {
      finish();
    }
  });
}

function audioContextConstructor() {
  // Prefer the global AudioContext binding (browser + Vitest patches on global).
  // Fall back to window/webkit for older Safari when the global is missing.
  if (typeof AudioContext !== 'undefined') return AudioContext;
  if (typeof window !== 'undefined') {
    return window.AudioContext || window.webkitAudioContext || null;
  }
  return null;
}

/**
 * Sanitize AudioContext.state to an enumerated diagnostic value only.
 * @param {AudioContext|null|undefined} ctx
 * @returns {'none'|'unknown'|'suspended'|'running'|'closed'|'interrupted'}
 */
function enumeratedAudioContextState(ctx) {
  if (!ctx) return 'none';
  const state = typeof ctx.state === 'string' ? ctx.state : '';
  if (AUDIO_CONTEXT_STATES.has(state)) return state;
  return 'unknown';
}

/**
 * Resume AudioContext until state is running when possible.
 * Never rebuilds the graph — only resumes the existing context.
 * Suspended after resume is recoverable (next gesture); closed is not.
 * @param {AudioContext|null|undefined} ctx
 * @returns {Promise<boolean>}
 */
async function ensureAudioContextRunning(ctx) {
  if (!ctx || ctx.state === 'closed') return false;
  if (ctx.state === 'running') return true;
  try {
    await ctx.resume();
  } catch {
    /* autoplay / closed races */
  }
  return ctx.state === 'running';
}

/**
 * Gesture-first unlock: create or resume a single AudioContext synchronously
 * on the click path (before accept API / getUserMedia awaits).
 * Idempotent: reuses prewarmed or live session context; never duplicates graph.
 * Optional callRecordId tags ownership for call-scoped cancel cleanup.
 * Never retags a holder already owned by a different callRecordId.
 * @param {number|string|null|undefined} [callRecordId]
 * @returns {AudioContext|null}
 */
export function prewarmStackZapAudioContext(callRecordId) {
  const ownedId =
    callRecordId != null && callRecordId !== '' ? callRecordId : null;

  // Live session already owns the graph — only nudge resume.
  // Do not transfer session ownership into the prewarm holder (would steal A).
  if (session?.audioContext && session.audioContext.state !== 'closed') {
    if (session.audioContext.state !== 'running') {
      session.audioContext.resume().catch(() => {});
    }
    return session.audioContext;
  }

  if (prewarmedAudioContext && prewarmedAudioContext.state !== 'closed') {
    // Ownership lease: unowned may be tagged once; same owner refreshes;
    // foreign callRecordId must NEVER retag/steal the holder.
    if (ownedId != null) {
      if (
        prewarmedCallRecordId == null ||
        sameCallRecordId(prewarmedCallRecordId, ownedId)
      ) {
        prewarmedCallRecordId = ownedId;
      }
    }
    if (prewarmedAudioContext.state !== 'running') {
      prewarmedAudioContext.resume().catch(() => {});
    }
    return prewarmedAudioContext;
  }

  const AC = audioContextConstructor();
  if (!AC) return null;

  try {
    prewarmedAudioContext = new AC();
    prewarmedCallRecordId = ownedId;
  } catch {
    prewarmedAudioContext = null;
    prewarmedCallRecordId = null;
    return null;
  }

  // Fire resume under residual user activation; do not await network here.
  if (prewarmedAudioContext.state !== 'running') {
    prewarmedAudioContext.resume().catch(() => {});
  }
  return prewarmedAudioContext;
}

/**
 * Transfer prewarmed context into a session, or construct one if prewarm missed.
 * Claims only unowned or same-call prewarm — never steals a foreign lease.
 * @param {number|string|null|undefined} [forCallRecordId]
 * @returns {AudioContext|null}
 */
function claimAudioContext(forCallRecordId) {
  let ctx = null;
  if (prewarmedAudioContext && prewarmedAudioContext.state !== 'closed') {
    const canClaim =
      prewarmedCallRecordId == null ||
      sameCallRecordId(prewarmedCallRecordId, forCallRecordId);
    if (canClaim) {
      ctx = prewarmedAudioContext;
      prewarmedAudioContext = null;
      prewarmedCallRecordId = null;
    }
  } else {
    prewarmedAudioContext = null;
    prewarmedCallRecordId = null;
  }

  if (!ctx) {
    // Foreign-owned prewarm left in place; this session gets its own graph.
    const AC = audioContextConstructor();
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  if (ctx.state !== 'running' && ctx.state !== 'closed') {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

function disposePrewarmedAudioContext() {
  if (!prewarmedAudioContext) {
    prewarmedCallRecordId = null;
    return;
  }
  const ctx = prewarmedAudioContext;
  prewarmedAudioContext = null;
  prewarmedCallRecordId = null;
  try {
    if (ctx.state !== 'closed') {
      ctx.close().catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

/**
 * Dispose prewarm only when it is still unowned (create-in-flight unlock).
 * Never closes a tagged foreign prewarm or an active session graph.
 * @returns {boolean}
 */
function disposeUnownedPrewarmOnly() {
  if (!prewarmedAudioContext) return false;
  if (prewarmedCallRecordId != null) return false;
  disposePrewarmedAudioContext();
  return true;
}

/**
 * Current prewarm owner id (tests / diagnostics). Null when unowned or empty.
 * @returns {number|string|null}
 */
function prewarmOwnerCallRecordId() {
  if (!prewarmedAudioContext || prewarmedAudioContext.state === 'closed') {
    return null;
  }
  return prewarmedCallRecordId;
}

function unbindAudioContextResumeHandlers(s) {
  if (!s?.audioResumeCleanup) return;
  try {
    s.audioResumeCleanup();
  } catch {
    /* ignore */
  }
  s.audioResumeCleanup = null;
}

/**
 * Keep a single AudioContext running across tab hide/show and user gestures.
 * Does not recreate nodes, MediaRecorder, or cable — resume only.
 * Recoverable suspension must never hang up; handlers stay bound until stop.
 */
function bindAudioContextResumeHandlers(epoch, s) {
  if (!s || typeof document === 'undefined') return;

  unbindAudioContextResumeHandlers(s);

  const tryResume = () => {
    if (!isCurrent(epoch) || !s.audioContext) return;
    if (s.audioContext.state === 'closed') return;
    if (s.audioContext.state === 'running') return;
    // suspended / interrupted — same context only.
    s.audioContext.resume().catch(() => {});
  };

  const onVisibility = () => {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      tryResume();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  // Gesture recovery for autoplay policy. Passive + capture: do not steal events
  // or rebuild the graph. pointer + touch + key cover desktop and mobile.
  const gestureOpts = { capture: true, passive: true };
  const gestureTypes = ['pointerdown', 'touchstart', 'keydown'];
  if (typeof window !== 'undefined') {
    gestureTypes.forEach(type => {
      window.addEventListener(type, tryResume, gestureOpts);
    });
  }

  s.audioResumeCleanup = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') {
      gestureTypes.forEach(type => {
        window.removeEventListener(type, tryResume, { capture: true });
      });
    }
  };

  // Immediate nudge if already suspended when handlers attach.
  tryResume();
}

function stopMicAndContext(s) {
  if (!s) return;
  unbindAudioContextResumeHandlers(s);
  try {
    if (s.processorNode) {
      s.processorNode.disconnect();
      s.processorNode.onaudioprocess = null;
    }
  } catch {
    /* ignore */
  }
  s.processorNode = null;
  try {
    s.sourceNode?.disconnect();
  } catch {
    /* ignore */
  }
  s.sourceNode = null;
  s.mixDestination = null;
  stopTracks(s.mediaStream);
  s.mediaStream = null;
  if (s.audioContext) {
    s.audioContext.close().catch(() => {});
    s.audioContext = null;
  }
  s.pendingSamples = [];
  s.nextPlayTime = 0;
}

/**
 * Drop only the ActionCable consumer/subscription (and heartbeat).
 * Preserves mic stream, AudioContext, MediaRecorder, and accumulated chunks.
 */
function teardownCable(s) {
  if (!s) return;
  s.suppressDisconnectHandler = true;
  try {
    s.subscription?.unsubscribe();
  } catch {
    /* ignore */
  }
  s.subscription = null;
  try {
    s.consumer?.disconnect();
  } catch {
    /* ignore */
  }
  s.consumer = null;
  if (s.heartbeatTimer) {
    clearInterval(s.heartbeatTimer);
    s.heartbeatTimer = null;
  }
  s.suppressDisconnectHandler = false;
}

// Reconnect helpers form a closed graph with fullStop/subscribeWithTimeout.
// Function declarations are hoisted; disable no-use-before-define for this block.
/* eslint-disable no-use-before-define */
function startHeartbeat(epoch, s) {
  if (!s || !isCurrent(epoch)) return;
  if (s.heartbeatTimer) {
    clearInterval(s.heartbeatTimer);
    s.heartbeatTimer = null;
  }
  s.heartbeatTimer = setInterval(() => {
    if (!isCurrent(epoch) || !s.active) return;
    try {
      s.subscription?.perform('heartbeat', {});
    } catch {
      if (isCurrent(epoch) && s.active) {
        scheduleResubscribe(epoch, s, 'heartbeat_failed');
      }
    }
  }, HEARTBEAT_MS);
}

/**
 * After an established session loses transport, resubscribe with backoff while
 * keeping the same mic/AudioContext/recorder/chunks. Bounded and deterministic.
 *
 * While resubscribeInFlight is true, disconnected/error must not be swallowed:
 * set needsResubscribe so the in-flight await re-enters backoff instead of
 * re-arming heartbeat on a subscription that already died.
 */
function scheduleResubscribe(epoch, s, reason) {
  if (!s || !isCurrent(epoch) || !s.active || s.stopping) return;

  // Latch drop observed during an in-flight resubscribe await. The await exit
  // path must observe this and retry; do not start a parallel subscription.
  if (s.resubscribeInFlight) {
    s.needsResubscribe = true;
    s.lastReconnectReason = reason || s.lastReconnectReason || 'disconnected';
    return;
  }

  // Avoid duplicate timers for the same session.
  if (s.reconnectTimer) return;

  const attempt = s.reconnectAttempt || 0;
  if (attempt >= RECONNECT_DELAYS_MS.length) {
    fullStop();
    return;
  }

  const delayMs = RECONNECT_DELAYS_MS[attempt];
  s.reconnectAttempt = attempt + 1;
  s.reconnecting = true;
  s.needsResubscribe = false;
  s.lastReconnectReason = reason || null;

  // Drop dead cable immediately so we never hold two leases/subscriptions.
  teardownCable(s);

  s.reconnectTimer = setTimeout(() => {
    s.reconnectTimer = null;
    if (!isCurrent(epoch) || !s.active || s.stopping) return;
    resubscribeTransport(epoch, s);
  }, delayMs);
}

async function resubscribeTransport(epoch, s) {
  if (!s || !isCurrent(epoch) || !s.active || s.stopping) return;
  if (s.resubscribeInFlight) return;

  s.resubscribeInFlight = true;
  s.reconnecting = true;
  // Fresh attempt: clear latch; any drop during this await re-sets it.
  s.needsResubscribe = false;

  // Ensure no leftover cable before creating a new one.
  teardownCable(s);

  if (!s.cableParams) {
    s.resubscribeInFlight = false;
    fullStop();
    return;
  }

  // Capture generation after teardown; subscribeWithTimeout bumps it for the
  // new subscription so stale callbacks from prior gens cannot latch/retry us.
  const generationBeforeSubscribe = s.cableGeneration || 0;

  s.consumer = createConsumer(cableUrl());
  try {
    await subscribeWithTimeout(epoch, s, s.cableParams, { isReconnect: true });
  } catch (err) {
    s.resubscribeInFlight = false;
    if (!isCurrent(epoch) || s.stopping) return;
    const code = err?.message || 'media_disconnected';
    // hadConnected: lease contention / codeless ActionCable rejected() retry
    // within the same RECONNECT_DELAYS_MS budget; auth still fails closed.
    if (isNonrecoverableErrorCode(code, { hadConnected: s.hadConnected })) {
      fullStop();
      return;
    }
    s.reconnecting = false;
    scheduleResubscribe(epoch, s, code);
    return;
  }

  if (!isCurrent(epoch) || s.stopping) {
    s.resubscribeInFlight = false;
    return;
  }

  // connected() may resolve while Cable immediately fires disconnected/error
  // on the same generation before this frame clears resubscribeInFlight.
  // needsResubscribe (or a generation that never advanced / already replaced)
  // means the transport of this attempt is not healthy — do not rearm heartbeat.
  const subscribedGeneration = s.cableGeneration || 0;
  const transportDropped =
    s.needsResubscribe ||
    !s.subscription ||
    subscribedGeneration <= generationBeforeSubscribe;

  if (transportDropped) {
    const reason = s.lastReconnectReason || 'disconnected';
    s.resubscribeInFlight = false;
    s.reconnecting = false;
    // Stay on the same bounded backoff; do not reset reconnectAttempt.
    scheduleResubscribe(epoch, s, reason);
    return;
  }

  // Transport recovered — re-arm sending/heartbeat; keep mic/recorder/chunks.
  s.resubscribeInFlight = false;
  s.reconnecting = false;
  s.needsResubscribe = false;
  s.reconnectAttempt = 0;
  s.active = true;
  s.settleConnect = null;
  startHeartbeat(epoch, s);
}
/* eslint-enable no-use-before-define */

function teardownSession(s) {
  if (!s) return;
  s.stopping = true;
  s.reconnecting = false;
  s.resubscribeInFlight = false;
  s.needsResubscribe = false;
  // Local teardown always settles a pending start as cancel (resolve).
  settlePendingConnect(s);
  clearSessionTimers(s);
  // Force-stop recorder without waiting when tearing down (pagehide / replace).
  if (s.mediaRecorder && s.mediaRecorder.state !== 'inactive') {
    try {
      s.mediaRecorder.stop();
    } catch {
      /* ignore */
    }
  }
  s.mediaRecorder = null;
  s.recorderChunks = [];
  stopMicAndContext(s);
  teardownCable(s);
  if (s.pageHideBound && typeof window !== 'undefined') {
    window.removeEventListener('pagehide', s.pageHideBound);
    s.pageHideBound = null;
  }
  s.active = false;
  s.muted = false;
}

function fullStop() {
  sessionEpoch += 1;
  const s = session;
  session = null;
  teardownSession(s);
  // Intentionally does NOT dispose prewarmedAudioContext: start() calls fullStop
  // first to drop a prior session, then claims the gesture-prewarmed context.
  // Explicit stop / end / cleanup paths dispose prewarmed after fullStop.
}

/**
 * Call-scoped release for cancel / dismiss paths.
 * Stops the live session only when it belongs to callRecordId.
 * Disposes prewarm only when explicitly tagged for the same call.
 * Never disposes an unowned prewarm (outbound create-in-flight unlock) just
 * because an unrelated call id was cancelled — that would steal another
 * call's gesture context. Never tears down a different active/stacked call.
 * Idempotent.
 * @param {number|string|null|undefined} callRecordId
 * @returns {boolean} true when something owned by this call was released
 */
function releaseMediaForCall(callRecordId) {
  if (callRecordId == null || callRecordId === '') return false;

  let released = false;

  if (session && sameCallRecordId(session.callRecordId, callRecordId)) {
    fullStop();
    released = true;
  }

  // Ownership-only: unowned prewarm is cleaned by create-fail via
  // disposeUnownedPrewarmOnly, not by foreign call.cancelled ids.
  if (
    prewarmedAudioContext &&
    prewarmedCallRecordId != null &&
    sameCallRecordId(prewarmedCallRecordId, callRecordId)
  ) {
    disposePrewarmedAudioContext();
    released = true;
  }

  return released;
}

/**
 * True when the live media session is active and owned by callRecordId.
 * Join uses this fail-closed gate so a silent/stale start cannot activate.
 * @param {number|string|null|undefined} callRecordId
 * @returns {boolean}
 */
function isActiveForCall(callRecordId) {
  if (callRecordId == null || callRecordId === '') return false;
  return Boolean(
    session?.active && sameCallRecordId(session.callRecordId, callRecordId)
  );
}

/**
 * Finalize recording then tear down media/mic. Always cleans up even if finalize fails.
 * Claimed/session context stays open until finalizeRecorder finishes above fullStop.
 * Pre-join end/cancel (no session) still drops the gesture-prewarmed context.
 * @returns {Promise<{ blob: Blob|null, recordingUnavailableReason: string|null, callRecordId: any }>}
 */
async function stopAndCollectRecording() {
  const s = session;
  const callRecordId = s?.callRecordId ?? null;
  const recordingUnavailableReason = s?.recordingUnavailableReason ?? null;
  let blob = null;
  try {
    if (s) blob = await finalizeRecorder(s);
  } catch {
    blob = null;
  } finally {
    fullStop();
    // endStackZapCall / remote hangup use this path; fullStop alone leaves an
    // unclaimed prewarmed context alive (orphaned process audio).
    disposePrewarmedAudioContext();
  }
  return {
    blob: blob && blob.size > 0 ? blob : null,
    recordingUnavailableReason,
    callRecordId,
  };
}

function schedulePlayback(s, floatFrame) {
  if (!s?.audioContext || !floatFrame?.length) return;
  if (floatFrame.length !== FRAME_SAMPLES) {
    fullStop();
    return;
  }

  // Mid-call suspend (background tab): nudge resume without rebuilding graph.
  if (s.audioContext.state === 'suspended') {
    s.audioContext.resume().catch(() => {});
  }

  const startAt = boundedNextPlaybackTime(
    s.audioContext.currentTime,
    s.nextPlayTime,
    LEAD_SECONDS
  );
  if (startAt == null) {
    // Already at playback horizon — drop frame; do not grow latency.
    return;
  }

  const buffer = s.audioContext.createBuffer(1, floatFrame.length, SAMPLE_RATE);
  buffer.getChannelData(0).set(floatFrame);
  const src = s.audioContext.createBufferSource();
  src.buffer = buffer;
  // Speaker path (existing).
  src.connect(s.audioContext.destination);
  // Recording mix (local already wired; remote PCM joins here).
  if (s.mixDestination) {
    try {
      src.connect(s.mixDestination);
    } catch {
      /* ignore single-frame mix failure */
    }
  }
  src.start(startAt);
  s.nextPlayTime = startAt + floatFrame.length / SAMPLE_RATE;
}

function rejectPendingConnect(epoch, message) {
  if (!isCurrent(epoch) || !session) return;
  settlePendingConnect(session, new Error(message));
}

function handleReceived(epoch, payload) {
  if (!isCurrent(epoch) || !session) return;
  if (!payload || typeof payload !== 'object') return;
  const s = session;

  if (payload.event === 'pcm' && typeof payload.pcm === 'string') {
    try {
      const ab = base64ToArrayBuffer(payload.pcm);
      if (!isExactFrameBytes(ab.byteLength)) {
        rejectPendingConnect(epoch, 'invalid_frame');
        fullStop();
        return;
      }
      const float32 = s16LEToFloat32(ab);
      if (!isCurrent(epoch)) return;
      schedulePlayback(session, float32);
    } catch {
      if (isCurrent(epoch)) {
        rejectPendingConnect(epoch, 'invalid_frame');
        fullStop();
      }
    }
    return;
  }
  if (payload.event === 'error') {
    const code =
      typeof payload.code === 'string' && payload.code.trim()
        ? payload.code.trim()
        : 'media_error';
    const hadConnected = Boolean(s.hadConnected || s.active);

    // Pending subscribe (initial handshake or reconnect handshake): settle
    // the connect promise with the typed code so callers can branch.
    // Initial (!active): always fullStop (fail-closed).
    // Reconnect (active): only fullStop for permanent codes; lease contention
    // rejects the promise so resubscribeTransport can retry within backoff.
    if (s.settleConnect) {
      const wasActive = s.active;
      rejectPendingConnect(epoch, code);
      if (!wasActive) {
        fullStop();
        return;
      }
      if (isNonrecoverableErrorCode(code, { hadConnected })) {
        fullStop();
      }
      // Recoverable while active: leave session alive; resubscribeTransport catch retries.
      return;
    }

    // Post-connect error (no pending settle): permanent → fullStop, else resubscribe.
    if (isNonrecoverableErrorCode(code, { hadConnected })) {
      fullStop();
      return;
    }
    scheduleResubscribe(epoch, s, code);
  }
}

async function startMicCapture(epoch, s, sendFrame) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
    },
  });

  // Stale getUserMedia: stop only this stream's tracks; never tear down a newer session.
  if (!isCurrent(epoch) || session !== s) {
    stopTracks(stream);
    throw mediaAbortError('session_stale');
  }

  s.mediaStream = stream;
  // Prefer gesture-prewarmed context owned by this call (or unowned lease).
  // Never steal a foreign prewarm lease — claim allocates a new graph then.
  const ctx = claimAudioContext(s.callRecordId);
  if (!ctx || ctx.state === 'closed') {
    stopTracks(stream);
    s.mediaStream = null;
    throw new Error('audio_context_unavailable');
  }
  s.audioContext = ctx;

  // Best-effort resume while residual activation may remain. If still
  // suspended after this await, do NOT hang up — bind recovery handlers and
  // keep the accepted call alive until the next pointer/touch/key/visibility.
  await ensureAudioContextRunning(s.audioContext);
  if (!isCurrent(epoch) || session !== s) {
    stopTracks(stream);
    // Context ownership stays on s for teardownSession; do not close here
    // if a newer session already claimed a different graph.
    s.mediaStream = null;
    throw mediaAbortError('session_stale');
  }

  s.sourceNode = s.audioContext.createMediaStreamSource(stream);

  // Fallback path: ScriptProcessorNode (broad compatibility).
  // Prefer AudioWorklet in a later slice once worklet assets are shipped.
  const bufferSize = 4096;
  s.processorNode = s.audioContext.createScriptProcessor(bufferSize, 1, 1);
  s.processorNode.onaudioprocess = event => {
    if (!isCurrent(epoch) || !s.active || s.muted) return;
    // Capture is a no-op while suspended; nudge resume without rebuilding graph.
    if (s.audioContext?.state === 'suspended') {
      s.audioContext.resume().catch(() => {});
      return;
    }
    const input = event.inputBuffer.getChannelData(0);
    let samples = input;
    try {
      samples = resampleTo16k(input, s.audioContext.sampleRate);
    } catch {
      return;
    }
    const frames = accumulateFrames(s.pendingSamples, samples);
    frames.forEach(b64 => sendFrame(b64));
  };
  s.sourceNode.connect(s.processorNode);
  s.processorNode.connect(s.audioContext.destination);
  bindAudioContextResumeHandlers(epoch, s);
}

function subscribeWithTimeout(epoch, s, params, { isReconnect = false } = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    // Generation guards stale subscription callbacks after intentional resubscribe.
    s.cableGeneration = (s.cableGeneration || 0) + 1;
    const generation = s.cableGeneration;

    const finish = err => {
      if (settled) return;
      settled = true;
      if (s.connectTimer) {
        clearTimeout(s.connectTimer);
        s.connectTimer = null;
      }
      if (s.settleConnect === finish) {
        s.settleConnect = null;
      }
      if (err) reject(err);
      else resolve();
    };

    // Idempotent handle also invoked by fullStop/teardownSession (local cancel).
    s.settleConnect = finish;

    s.connectTimer = setTimeout(() => {
      finish(new Error('connect_timeout'));
    }, CONNECT_TIMEOUT_MS);

    s.subscription = s.consumer.subscriptions.create(params, {
      connected() {
        if (!isCurrent(epoch) || s.cableGeneration !== generation) {
          finish(new Error('session_stale'));
          return;
        }
        finish();
      },
      rejected() {
        if (s.cableGeneration !== generation) return;
        // ActionCable rejected() carries no application code. Initial connect
        // maps this to fatal media_rejected (fail-closed). After hadConnected,
        // resubscribeTransport treats media_rejected as lease-contention retry
        // within RECONNECT_DELAYS_MS (see RECONNECT_CONTENTION_CODES).
        finish(new Error('media_rejected'));
      },
      disconnected() {
        if (s.cableGeneration !== generation) return;
        if (s.suppressDisconnectHandler) return;
        if (!isCurrent(epoch)) {
          // Epoch already cancelled; ensure any race still settles once.
          if (!settled) finish(new Error('media_disconnected'));
          return;
        }
        if (!settled) {
          finish(new Error('media_disconnected'));
          return;
        }
        // Initial connect path never recovers after settle-before-active;
        // established active sessions recover via bounded resubscribe.
        if (s.active && !s.stopping && !isReconnect) {
          scheduleResubscribe(epoch, s, 'disconnected');
          return;
        }
        if (s.active && !s.stopping && isReconnect) {
          // Reconnect subscription itself dropped after connect — try again.
          scheduleResubscribe(epoch, s, 'disconnected');
          return;
        }
        if (!s.active) {
          fullStop();
        }
      },
      received(data) {
        if (s.cableGeneration !== generation) return;
        handleReceived(epoch, data);
      },
    });
  });
}

export function useStackZapMediaSession() {
  const start = async ({ pubsubToken, userId, accountId, callRecordId }) => {
    // Bump epoch and tear down any prior session first (settles stale start once).
    // Intentionally replaces the single live media graph; create-fail paths must
    // never call this for a foreign call — use releaseForCall / disposeUnowned.
    fullStop();

    if (!pubsubToken || !userId || !accountId || !callRecordId) {
      throw new Error('missing_session_params');
    }

    const epoch = sessionEpoch + 1;
    sessionEpoch = epoch;
    const s = {
      epoch,
      callRecordId,
      consumer: null,
      subscription: null,
      mediaStream: null,
      audioContext: null,
      processorNode: null,
      sourceNode: null,
      mixDestination: null,
      mediaRecorder: null,
      recorderChunks: [],
      recorderMimeType: null,
      recordingStarted: false,
      recorderFinalized: false,
      finalRecordingBlob: null,
      recordingUnavailableReason: null,
      heartbeatTimer: null,
      reconnectTimer: null,
      reconnectAttempt: 0,
      reconnecting: false,
      resubscribeInFlight: false,
      /**
       * Durable latch: set when disconnected/error arrives while a resubscribe
       * await is in flight, so the drop is never swallowed by the inFlight guard.
       */
      needsResubscribe: false,
      lastReconnectReason: null,
      /** True after the first successful media connect for this session. */
      hadConnected: false,
      cableGeneration: 0,
      cableParams: null,
      suppressDisconnectHandler: false,
      stopping: false,
      pageHideBound: null,
      audioResumeCleanup: null,
      pendingSamples: [],
      nextPlayTime: 0,
      muted: false,
      active: false,
      connectTimer: null,
      settleConnect: null,
    };
    session = s;

    const sendFrame = b64 => {
      if (!isCurrent(epoch) || !s.subscription || !s.active || s.reconnecting) {
        return;
      }
      try {
        s.subscription.perform('audio', { pcm: b64 });
      } catch {
        if (isCurrent(epoch) && s.active) {
          scheduleResubscribe(epoch, s, 'send_failed');
        }
      }
    };

    // C3 — mic before ActionCable/provider lease so a slow permission prompt
    // does not consume the 60s lease / upstream socket.
    try {
      await startMicCapture(epoch, s, sendFrame);
    } catch (err) {
      // Fail-closed: never resolve successfully without owned active media.
      // session_stale / media_aborted = epoch killed (cancel / replace).
      const code = err?.code || err?.message;
      if (code === 'session_stale' || code === 'media_aborted') {
        if (isCurrent(epoch)) fullStop();
        throw mediaAbortError(
          code === 'media_aborted' ? 'media_aborted' : 'session_stale'
        );
      }
      if (isCurrent(epoch)) fullStop();
      // Typed fail-closed only when Web Audio cannot be constructed at all.
      // Recoverable suspension never reaches here — call stays alive.
      if (err?.message === 'audio_context_unavailable') {
        throw new Error('audio_context_unavailable');
      }
      throw new Error('mic_failed');
    }

    if (!isCurrent(epoch)) {
      stopTracks(s.mediaStream);
      s.mediaStream = null;
      throw mediaAbortError('session_stale');
    }

    s.consumer = createConsumer(cableUrl());
    const params = {
      channel: 'StackZapMediaChannel',
      pubsub_token: pubsubToken,
      user_id: userId,
      account_id: accountId,
      call_record_id: callRecordId,
    };
    s.cableParams = params;

    try {
      await subscribeWithTimeout(epoch, s, params, { isReconnect: false });
    } catch (err) {
      if (isCurrent(epoch)) fullStop();
      throw err;
    }

    if (!isCurrent(epoch)) {
      // Orphan partial session (cancel during cable): tear local resources only.
      teardownSession(s);
      throw mediaAbortError('session_stale');
    }

    s.active = true;
    s.hadConnected = true;
    s.reconnectAttempt = 0;
    s.reconnecting = false;
    // Connect settled successfully — drop handle so later fullStop does not re-resolve.
    s.settleConnect = null;
    // Media transport is up; recorder arms only when call session reports connected/in_progress
    // (see startRecording). Outbound may ring with media open before remote answer.

    startHeartbeat(epoch, s);

    if (typeof window !== 'undefined') {
      s.pageHideBound = () => {
        if (isCurrent(epoch)) fullStop();
      };
      window.addEventListener('pagehide', s.pageHideBound);
    }

    // Final ownership proof before resolve — close TOCTOU with cancel mid-start.
    if (!isCurrent(epoch) || !s.active) {
      teardownSession(s);
      throw mediaAbortError('media_aborted');
    }
  };

  const stop = () => {
    fullStop();
    disposePrewarmedAudioContext();
  };

  /**
   * Arm MediaRecorder once after durable connected/in_progress evidence.
   * Safe to call repeatedly; returns whether a new recorder was started.
   */
  const startRecording = () => {
    if (!session?.active) return false;
    return armRecorder(session);
  };

  const stopAndGetRecording = () => stopAndCollectRecording();

  const setMuted = value => {
    if (!session) return;
    session.muted = Boolean(value);
    if (session.mediaStream) {
      session.mediaStream.getAudioTracks().forEach(t => {
        t.enabled = !session.muted;
      });
    }
  };

  const isMuted = () => Boolean(session?.muted);
  const isActive = () => Boolean(session?.active);
  const isRecording = () =>
    Boolean(
      session?.mediaRecorder && session.mediaRecorder.state === 'recording'
    );
  const recordingUnavailableReason = () =>
    session?.recordingUnavailableReason || null;

  /**
   * Enumerated AudioContext state for diagnostics/tests only (no payload/PII).
   * Prefers live session context, then prewarmed holder.
   */
  const audioContextState = () =>
    enumeratedAudioContextState(session?.audioContext || prewarmedAudioContext);

  return {
    start,
    stop,
    prewarmAudioContext: prewarmStackZapAudioContext,
    /**
     * Call-scoped cancel cleanup: only prewarm/session owned by callRecordId.
     * Safe for stacked widgets — does not stop a foreign active call.
     */
    releaseForCall: releaseMediaForCall,
    /**
     * Create-fail path: drop unowned create-in-flight prewarm only.
     * Never touches an active session or a foreign-tagged prewarm.
     */
    disposeUnownedPrewarm: disposeUnownedPrewarmOnly,
    /** Live session active + owned by this callRecordId (join fail-closed). */
    isActiveForCall,
    /** Current prewarm owner id (null when unowned / empty). */
    prewarmOwnerCallRecordId,
    startRecording,
    stopAndGetRecording,
    setMuted,
    isMuted,
    isActive,
    isRecording,
    recordingUnavailableReason,
    audioContextState,
    // constants useful for integrators / tests
    FRAME_SAMPLES,
    SAMPLE_RATE,
    FRAME_BYTES,
  };
}

function cleanupStackZapMediaSessionImpl() {
  fullStop();
  disposePrewarmedAudioContext();
}

export {
  cleanupStackZapMediaSessionImpl as cleanupStackZapMediaSession,
  stopAndCollectRecording,
};
